import type { ElementType, FC } from "react";

import {
  AgentsIcon,
  AudienceIcon,
  Box,
  Column,
  ExperimentIcon,
  InsightsIcon,
  JourneyIcon,
  ModelIcon,
  PerformanceIcon,
  Row,
  Skeleton,
  SkeletonBox,
  SyncIcon,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import { useUser } from "src/contexts/user-context";
import { usePersistedState } from "src/hooks/use-persisted-state";
import { Icon as AdStudioIcon } from "src/components/layout/nav/icons/ad-studio";
import { Icon as LifecycleStudioIcon } from "src/components/layout/nav/icons/lifecycle-studio";
import { getPersistedKey } from "src/pages/home/components/use-recently-viewed";
import type { Meta } from "src/pages/home/route-utils";

import { LIST_SECTION_LABEL_PROPS } from "src/pages/home/agentic/styles";

import { trackHomeLinkClick } from "src/pages/home/agentic/home-tracking";
import {
  HOME_CARD_EDGE,
  useIsHomeInCard,
} from "src/pages/home/agentic/components/home-in-card-context";

import { HoverableListItem } from "./hoverable-list-item";
import { useRecentlyViewedNames } from "./recently-viewed-names";
import {
  MAX_ITEMS,
  MAX_ITEMS_COMPACT,
  rankRecentlyViewed,
} from "./recently-viewed-rank";

export const RESOURCE_ICONS: Record<Meta["resource"], ElementType> = {
  audiences: AudienceIcon,
  journeys: JourneyIcon,
  syncs: SyncIcon,
  models: ModelIcon,
  charts: InsightsIcon,
  dashboards: PerformanceIcon,
  experiments: ExperimentIcon,
  campaigns: LifecycleStudioIcon,
  "ad-studio-projects": AdStudioIcon,
  reports: AgentsIcon,
};

export const getHref = (meta: Meta): string => {
  switch (meta.resource) {
    case "audiences":
      return `/audiences/${meta.id}`;
    case "journeys":
      return `/journeys/${meta.id}`;
    case "syncs":
      return `/syncs/${meta.id}`;
    case "models":
      return `/models/${meta.id}`;
    case "charts":
      return `/analytics/${meta.id}`;
    case "dashboards":
      return `/dashboards/${meta.id}`;
    case "experiments":
      return `/experiments/${meta.id}`;
    case "campaigns":
      return `/lifecycle-studio/projects/${meta.id}`;
    case "ad-studio-projects":
      return `/ad-studio/projects/${meta.id}`;
    case "reports":
      return `/agents/reports/${meta.id}/runs`;
  }
};

type RecentlyViewedListProps = {
  compact?: boolean;
};

export const RecentlyViewedList: FC<RecentlyViewedListProps> = ({
  compact = false,
}) => {
  const isInCard = useIsHomeInCard();
  const { workspace } = useUser();
  const marketing = usePersistedState<Meta[]>(
    getPersistedKey(workspace?.id, "marketing"),
    [],
  );
  const activation = usePersistedState<Meta[]>(
    getPersistedKey(workspace?.id, "activation"),
    [],
  );
  const studio = usePersistedState<Meta[]>(
    getPersistedKey(workspace?.id, "studio"),
    [],
  );

  const recentlyViewed = rankRecentlyViewed(
    marketing.value,
    activation.value,
    studio.value,
  );

  const { resolveName, isLoading } = useRecentlyViewedNames(recentlyViewed);

  if (recentlyViewed.length === 0) {
    return null;
  }

  const visibleCount = compact ? MAX_ITEMS_COMPACT : MAX_ITEMS;
  const items = recentlyViewed
    .map((meta) => ({ meta, name: resolveName(meta) }))
    .filter((item): item is { meta: Meta; name: string } => item.name !== null)
    .slice(0, visibleCount);

  // Names resolved but nothing matched (resources deleted) — nothing to show.
  if (!isLoading && items.length === 0) {
    return null;
  }

  return (
    <Column gap={2} width="100%">
      <Text {...LIST_SECTION_LABEL_PROPS}>Recently viewed</Text>
      <Column gap={1} width="100%">
        {isLoading
          ? recentlyViewed.slice(0, visibleCount).map((meta) => (
              <Skeleton isLoading key={`${meta.resource}-${meta.id}`}>
                <SkeletonBox height="48px" borderRadius="xl" />
              </Skeleton>
            ))
          : items.map(({ meta, name }) => {
              const ResourceIcon = RESOURCE_ICONS[meta.resource];
              const href = getHref(meta);
              return (
                <HoverableListItem
                  key={`${meta.resource}-${meta.id}`}
                  to={href}
                  onClick={() =>
                    trackHomeLinkClick({
                      section: "recently_viewed",
                      label: name,
                      href,
                    })
                  }
                >
                  <Column
                    align="center"
                    justify="center"
                    boxSize="32px"
                    flexShrink={0}
                    bg="white"
                    borderRadius="lg"
                    boxShadow="xs"
                    {...(isInCard && HOME_CARD_EDGE)}
                  >
                    <Box as={ResourceIcon} boxSize={4} color="text.secondary" />
                  </Column>
                  <Row flex={1} minWidth={0}>
                    <TextWithTooltip color="text.secondary">
                      {name}
                    </TextWithTooltip>
                  </Row>
                </HoverableListItem>
              );
            })}
      </Column>
    </Column>
  );
};
